import { useState, useEffect, useCallback, useRef, lazy, Suspense } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { m, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

const SearchFilters = lazy(() => import("./SearchFilters"));

interface Slide {
  id: string;
  video: string;
  gradient: string;
  titleFr: string;
  titleEn: string;
  subtitleFr: string;
  subtitleEn: string;
}

const slides: Slide[] = [
  {
    id: "power",
    video: "/videos/slide-power.mp4",
    gradient: "from-[hsl(220,30%,15%)] via-[hsl(210,25%,20%)] to-[hsl(200,20%,12%)]",
    titleFr: "Un grand pouvoir implique de grandes responsabilités.",
    titleEn: "With great power comes great responsibility.",
    subtitleFr: "Chaque achat est un vote pour le monde de demain. Reprenez le contrôle.",
    subtitleEn: "Every purchase is a vote for tomorrow's world. Take back control.",
  },
  {
    id: "verdict",
    video: "/videos/slide-verdict.mp4",
    gradient: "from-[hsl(30,20%,18%)] via-[hsl(25,15%,22%)] to-[hsl(20,10%,14%)]",
    titleFr: "Coton recyclé, production locale ou commerce équitable ?",
    titleEn: "Recycled cotton, local production or fair trade?",
    subtitleFr: "Nous décodons le marketing pour révéler l'impact réel derrière chaque étiquette.",
    subtitleEn: "We decode marketing to reveal the real impact behind every label.",
  },
  {
    id: "cool",
    video: "/videos/slide-cool.mp4",
    gradient: "from-[hsl(180,15%,14%)] via-[hsl(170,12%,18%)] to-[hsl(160,10%,12%)]",
    titleFr: "Redéfinissons ensemble ce qui est cool.",
    titleEn: "Let's redefine what's cool together.",
    subtitleFr: "Le vrai luxe, c'est l'intégrité. Ne portez plus seulement une marque, portez vos valeurs.",
    subtitleEn: "True luxury is integrity. Don't just wear a brand, wear your values.",
  },
  {
    id: "ocean",
    video: "/videos/slide-ocean.mp4",
    gradient: "from-[hsl(200,30%,14%)] via-[hsl(195,25%,18%)] to-[hsl(210,20%,12%)]",
    titleFr: "Soyez le héros du quotidien.",
    titleEn: "Be the everyday hero.",
    subtitleFr: "Vos petites gouttes d'eau créent l'océan du changement. Remettez-vous au centre de l'économie.",
    subtitleEn: "Your small drops of water create the ocean of change. Put yourself back at the center of the economy.",
  },
];

/** Only mount slide 0 immediately. Slide 1 after 1s. Slides 2-3 after 4s */
const BATCH_1 = new Set([0]);

const HeroSlider = () => {
  const { language } = useLanguage();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [prevSlide, setPrevSlide] = useState<number | null>(null);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [videoErrors, setVideoErrors] = useState<Record<string, boolean>>({});
  const [videoReady, setVideoReady] = useState<Record<string, boolean>>({});
  const [batch2Loaded, setBatch2Loaded] = useState(false);
  const transitionRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Staggered load: slide 1 after 2s, slides 2-3 after 4s
  const [slide1Loaded, setSlide1Loaded] = useState(false);
  useEffect(() => {
    const t1 = setTimeout(() => setSlide1Loaded(true), 2000);
    const t2 = setTimeout(() => setBatch2Loaded(true), 4000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  // Determine which indices are mounted
  const isMounted = useCallback(
    (index: number) => {
      // Current & previous always mounted (for cross-fade)
      if (index === currentSlide) return true;
      if (index === prevSlide) return true;
      // First slide always
      if (index === 0) return true;
      // Slide 1 after delay
      if (index === 1) return slide1Loaded;
      // Slides 2-3 after longer delay
      return batch2Loaded;
    },
    [currentSlide, prevSlide, slide1Loaded, batch2Loaded]
  );

  const changeSlide = useCallback((next: number) => {
    setPrevSlide((prev) => {
      // Clear previous transition timer
      if (transitionRef.current) clearTimeout(transitionRef.current);
      return undefined as unknown as number;
    });
    setPrevSlide(currentSlide);
    setCurrentSlide(next);
    // Clear prevSlide after cross-fade completes (800ms)
    transitionRef.current = setTimeout(() => setPrevSlide(null), 900);
  }, [currentSlide]);

  const goToSlide = useCallback((index: number) => {
    changeSlide(index);
  }, [changeSlide]);

  const goToPrevious = useCallback(() => {
    const prev = currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
    changeSlide(prev);
    setIsAutoPlaying(false);
  }, [currentSlide, changeSlide]);

  const goToNext = useCallback(() => {
    const next = currentSlide === slides.length - 1 ? 0 : currentSlide + 1;
    changeSlide(next);
    setIsAutoPlaying(false);
  }, [currentSlide, changeSlide]);

  // Auto-play
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentSlide((cur) => {
        const next = cur === slides.length - 1 ? 0 : cur + 1;
        setPrevSlide(cur);
        if (transitionRef.current) clearTimeout(transitionRef.current);
        transitionRef.current = setTimeout(() => setPrevSlide(null), 900);
        return next;
      });
    }, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  // Resume autoplay
  useEffect(() => {
    if (isAutoPlaying) return;
    const timeout = setTimeout(() => setIsAutoPlaying(true), 10000);
    return () => clearTimeout(timeout);
  }, [isAutoPlaying, currentSlide]);

  const handleVideoReady = useCallback((slideId: string) => {
    setVideoReady((prev) => ({ ...prev, [slideId]: true }));
  }, []);

  return (
    <section className="relative min-h-[70vh] md:h-[80vh] w-full overflow-visible bg-black">
      {/* All slide layers – cross-fade via z-index + opacity */}
      {slides.map((slide, index) => {
        const mounted = isMounted(index);
        const isActive = index === currentSlide;
        const isExiting = index === prevSlide;
        const hasError = videoErrors[slide.id];
        const isReady = videoReady[slide.id];

        // z-index: exiting slide behind, active on top
        let zIndex = 0;
        if (isExiting) zIndex = 5;
        if (isActive) zIndex = 10;

        return (
          <div
            key={slide.id}
            className="absolute inset-0"
            style={{
              zIndex,
              opacity: isActive || isExiting ? 1 : 0,
              transition: "opacity 0.8s ease-in-out",
            }}
          >
            {/* Gradient placeholder – always present as safety net */}
            <div className={`absolute inset-0 bg-gradient-to-br ${slide.gradient}`} />

            {/* Video layer */}
            {mounted && !hasError && (
              <video
                autoPlay
                loop
                muted
                playsInline
                preload={index === 0 ? "auto" : "none"}
                // @ts-ignore
                fetchpriority={index === 0 ? "high" : undefined}
                className="absolute inset-0 w-full h-full object-cover object-center"
                style={{
                  opacity: isReady ? 1 : 0,
                  transition: "opacity 0.6s ease-in-out",
                }}
                onCanPlayThrough={() => handleVideoReady(slide.id)}
                onError={() => setVideoErrors((prev) => ({ ...prev, [slide.id]: true }))}
              >
                <source src={slide.video} type="video/mp4" />
              </video>
            )}

            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-black/60" />
          </div>
        );
      })}

      {/* Text Content with cross-fade */}
      <AnimatePresence mode="popLayout">
        <m.div
          key={currentSlide}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 pb-16 md:pb-20"
        >
          <div className="max-w-4xl mx-auto text-center flex flex-col gap-4 md:gap-6">
            <m.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="font-display text-xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
            >
              {language === "fr" ? slides[currentSlide].titleFr : slides[currentSlide].titleEn}
            </m.h1>

            <m.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="font-sans text-base sm:text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed"
            >
              {language === "fr" ? slides[currentSlide].subtitleFr : slides[currentSlide].subtitleEn}
            </m.p>
          </div>
        </m.div>
      </AnimatePresence>

      {/* Fixed Search Bar - lazy loaded to reduce TBT */}
      <div className="absolute bottom-16 md:bottom-20 left-0 right-0 z-30 px-6">
        <Suspense fallback={<div className="h-12" />}>
          <SearchFilters />
        </Suspense>
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="hidden md:block absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all duration-300"
        aria-label="Previous slide"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      <button
        onClick={goToNext}
        className="hidden md:block absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all duration-300"
        aria-label="Next slide"
      >
        <ChevronRight className="w-6 h-6" />
      </button>

      {/* Dot Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {slides.map((slide, index) => (
          <button
            key={slide.id}
            onClick={() => {
              goToSlide(index);
              setIsAutoPlaying(false);
            }}
            className={`relative w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide
                ? "bg-white scale-110"
                : "bg-white/40 hover:bg-white/60"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          >
            {index === currentSlide && isAutoPlaying && (
              <m.div
                className="absolute inset-0 rounded-full border-2 border-white"
                initial={{ scale: 1, opacity: 1 }}
                animate={{ scale: 1.5, opacity: 0 }}
                transition={{ duration: 6, repeat: Infinity }}
              />
            )}
          </button>
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
